import { useCallback, useRef, useState } from 'react';

import { Button, Text } from '@ekhodealer/ekho-common/rev';

import './CopyableCodeBlock.scss';

interface CopyableCodeBlockProps {
	/** Label shown in the header (e.g. "HTML", "Service account") */
	label: string;
	/** The string to display in the code block and copy to clipboard */
	value: string;
}

export function CopyableCodeBlock({ label, value }: CopyableCodeBlockProps): React.ReactNode {
	const [copied, setCopied] = useState(false);
	const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

	const handleCopy = useCallback(async () => {
		try {
			await navigator.clipboard.writeText(value);
			setCopied(true);
			if (timerRef.current) clearTimeout(timerRef.current);
			timerRef.current = setTimeout(() => setCopied(false), 2000);
		} catch {
			// Clipboard write failed — silently ignore
		}
	}, [value]);

	return (
		<div data-component='copyable-code-block'>
			<div className='copyable-code-block__header'>
				<Text size='caption' color='secondary'>
					{label}
				</Text>
				<Button
					type='button'
					variant='ghost'
					size='small'
					className={`copyable-code-block__copy${copied ? ' copyable-code-block__copy--copied' : ''}`}
					onPress={handleCopy}>
					{copied ? (
						<Text size='caption' color='success'>
							Copied
						</Text>
					) : (
						<Text size='caption' color='secondary'>
							Copy
						</Text>
					)}
				</Button>
			</div>
			<pre className='copyable-code-block__pre'>
				<code>{value}</code>
			</pre>
		</div>
	);
}
